import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginAdmin, getAdminMe } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { showError, showSuccess } from '@/utils/swal';
import SliderCaptcha from './components/SliderCaptcha';

export default function AdminLoginPage() {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaReset, setCaptchaReset] = useState(false);

  const handleCaptchaVerify = useCallback((verified: boolean) => {
    setCaptchaVerified(verified);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;

    if (!captchaVerified) {
      showError(t('admin_login_error'), t('admin_login_slider_error'));
      return;
    }

    setLoading(true);
    try {
      const res = await loginAdmin({ email: email.trim(), password });
      if (!res.success || !res.data) {
        showError(t('admin_login_error'), res.message || t('admin_login_invalid'));
        setLoading(false);
        setCaptchaReset((v) => !v);
        setCaptchaVerified(false);
        return;
      }

      // Validate session via /admin/me.php before redirecting
      const me = await getAdminMe();
      if (!me) {
        showError(t('admin_login_error'), 'Session validation failed');
        setLoading(false);
        setCaptchaReset((v) => !v);
        setCaptchaVerified(false);
        return;
      }

      setUser(me);
      showSuccess('Login Successful', t('admin_login_submit'));
      navigate('/admin', { replace: true });
    } catch {
      showError(t('admin_login_error'), t('admin_login_error'));
      setLoading(false);
      setCaptchaReset((v) => !v);
      setCaptchaVerified(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0F2F5] px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 md:p-10 shadow-sm">
          <div className="flex flex-col items-center mb-8">
            <div className="w-14 h-14 rounded-xl bg-brand-mint flex items-center justify-center mb-4">
              <i className="ri-shield-check-line text-white text-2xl" />
            </div>
            <h1 className="text-xl font-bold text-brand-dark font-heading">
              {t('admin_website_admin')}
            </h1>
            <p className="text-sm text-brand-muted mt-1">
              {t('admin_login_title')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-1.5">
                {t('admin_login_email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('admin_login_email_placeholder')}
                required
                autoComplete="email"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-mint/50 focus:border-brand-mint transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-dark mb-1.5">
                {t('admin_login_password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('admin_login_password_placeholder')}
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-11 rounded-lg border border-gray-200 bg-white text-sm text-brand-dark placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-mint/50 focus:border-brand-mint transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('admin_login_hide_password') : t('admin_login_show_password')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-brand-dark transition-colors cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-dark mb-1.5">
                {t('admin_login_captcha')}
              </label>
              <SliderCaptcha onVerify={handleCaptchaVerify} reset={captchaReset} />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-brand-dark text-white text-sm font-semibold rounded-lg hover:bg-brand-dark/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  {t('admin_login_loading')}
                </>
              ) : (
                <>
                  <i className="ri-login-box-line" />
                  {t('admin_login_submit')}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-dark transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t('admin_back_website')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}